// Two dashboard widgets that show a project's own tabs on a dashboard: its Process (the accordion,
// with every action control and its two-half permission checks) and its Quality Plan. They exist for
// the PM Dashboard's Process and Quality Plan pages.
//
// Each mounts the SAME editor the project dialog uses, so the dashboard can never drift from the tab.
// The editors are imported on first render, not at registration: registration runs from ScriptInit on
// every page, and neither editor should be downloaded by a page that never draws one.
//
// The project comes from the dashboard's `ProjectId` parameter (the PM Dashboard's query string). The
// render context carries it in its scope snapshot, the same place a cell binding's ${ProjectId} is
// resolved from.
//
// The contract is a structural mirror of the library's, for the reason processPortfolioWidget.ts gives:
// the published package exports no type a consumer can import.
//
// NOTE: the Process widget is a KeyView accommodation. A generic "process for this record" widget has
// been requested from the process library (Requirements/SisolcoLibraryRequests/ProcessRecordDashboardWidget.md);
// when it lands, the PM Dashboard's cell switches widget key and this one is removed.

import type { PropertyItem } from "@serenity-is/corelib";

// Declared here rather than imported, so that this module (loaded on every page through ScriptInit)
// pulls in nothing beyond itself until a cell draws.
const PROJECT_ENTITY_TYPE = "KeyView.Projects.ProjectHeaderRow";

export const ProjectProcessWidgetKey = "KeyView.ProjectProcess";
export const ProjectQualityPlanWidgetKey = "KeyView.ProjectQualityPlan";

/** The part of the library's WidgetRenderContext these widgets use. */
export interface ProjectWidgetRenderContext {
    getScopeSnapshot?(): Record<string, unknown> | undefined;
    editMode?: boolean;
    localize?(text: string | null | undefined): string;
}

/** The config an author edits. Only a heading; the widget has nothing else to configure. */
export interface ProjectEditorWidgetConfig {
    Title?: string | null;
}

/** What a mounted editor must offer. Both the process accordion and the quality plan editor do. */
export interface MountedProjectEditor {
    readonly domNode: HTMLElement;
    setEditValue(source: unknown, property: PropertyItem): void;
    destroy?(): void;
}

export type ProjectEditorFactory = () => Promise<MountedProjectEditor>;

const editorFactories: Record<string, ProjectEditorFactory> = {
    [ProjectProcessWidgetKey]: async () => {
        const { ProcessChartEditor } = await import("../../Common/Process/ProcessChartEditor");
        return new ProcessChartEditor({ entityTypeName: PROJECT_ENTITY_TYPE }) as unknown as MountedProjectEditor;
    },
    [ProjectQualityPlanWidgetKey]: async () => {
        const { QualityPlanEditor } = await import("../QualityPlan/QualityPlanEditor");
        return new QualityPlanEditor({ entityTypeName: PROJECT_ENTITY_TYPE }) as unknown as MountedProjectEditor;
    },
};

const defaultTitles: Record<string, string> = {
    [ProjectProcessWidgetKey]: "Process",
    [ProjectQualityPlanWidgetKey]: "Quality plan",
};

/** The dashboard's ProjectId parameter as a number, or null when the dashboard was opened without one. */
export function readProjectId(ctx: ProjectWidgetRenderContext | null | undefined): number | null {
    const snapshot = ctx?.getScopeSnapshot?.();
    const flat = snapshot?.["ProjectId"];
    const nested = (snapshot?.["parameters"] as Record<string, unknown> | undefined)?.["ProjectId"];
    const raw = flat ?? nested;
    const id = typeof raw === "string" ? parseInt(raw, 10) : raw;
    return typeof id === "number" && !isNaN(id) && id > 0 ? id : null;
}

export class ProjectEditorWidget {
    private host: HTMLElement | null = null;
    private editor: MountedProjectEditor | null = null;
    /** Guards against a slow editor import finishing after the cell was disposed or re-rendered. */
    private renderToken = 0;

    constructor(readonly widgetTypeKey: string,
        private readonly factory: ProjectEditorFactory = editorFactories[widgetTypeKey]) {
    }

    render(host: HTMLElement, config: ProjectEditorWidgetConfig, _data: unknown, ctx: ProjectWidgetRenderContext): void {
        this.host = host;
        void this.draw(config, ctx);
    }

    update(config: ProjectEditorWidgetConfig, _data: unknown): void {
        // The editor re-reads on its own actions; a config change only touches the heading.
        const title = this.host?.querySelector<HTMLElement>(".keyview-project-widget__title");
        if (title) title.textContent = this.titleOf(config);
    }

    dispose(): void {
        this.renderToken++;
        this.editor?.destroy?.();
        this.editor = null;
        if (this.host) this.host.textContent = "";
        this.host = null;
    }

    private titleOf(config: ProjectEditorWidgetConfig | null | undefined): string {
        return config?.Title || defaultTitles[this.widgetTypeKey] || "";
    }

    private async draw(config: ProjectEditorWidgetConfig, ctx: ProjectWidgetRenderContext): Promise<void> {
        const host = this.host;
        if (!host) return;
        const token = ++this.renderToken;

        host.textContent = "";
        const root = document.createElement("div");
        root.className = "keyview-project-widget";

        const title = document.createElement("div");
        title.className = "keyview-project-widget__title title";
        title.textContent = this.titleOf(config);
        root.appendChild(title);

        const body = document.createElement("div");
        body.className = "keyview-project-widget__body";
        root.appendChild(body);
        host.appendChild(root);

        // In the editor the cell shows what it will hold, not live controls an author could act on.
        if (ctx?.editMode) {
            this.note(body, `The project's ${this.titleOf(config).toLowerCase()} is shown here when the dashboard is viewed.`);
            return;
        }

        const projectId = readProjectId(ctx);
        if (projectId == null) {
            this.note(body, "Open this dashboard from a project to see its " + this.titleOf(config).toLowerCase() + ".");
            return;
        }

        let editor: MountedProjectEditor;
        try {
            editor = await this.factory();
        }
        catch {
            if (token === this.renderToken) this.note(body, "This view could not be loaded.");
            return;
        }

        // Disposed or redrawn while the editor was loading.
        if (token !== this.renderToken || this.host !== host) {
            editor.destroy?.();
            return;
        }

        this.editor = editor;
        body.appendChild(editor.domNode);
        editor.setEditValue({ Id: projectId }, { name: "Id" } as PropertyItem);
    }

    private note(body: HTMLElement, text: string): void {
        const note = document.createElement("div");
        note.className = "keyview-project-widget__note";
        note.textContent = text;
        body.appendChild(note);
    }
}
